/**
 * audioWav — small helpers to encode/decode WAV via the Web Audio API.
 * Used by the DAW and the Sample Library (slice tracks into a pack).
 */

/** Encode an AudioBuffer (mono or stereo) into a 16-bit PCM WAV Blob. */
export function audioBufferToWavBlob(buffer) {
  const numCh = buffer.numberOfChannels;
  const sr = buffer.sampleRate;
  const samples = buffer.length;
  const dataSize = samples * numCh * 2;
  const ab = new ArrayBuffer(44 + dataSize);
  const v = new DataView(ab);
  const w = (o, s) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
  w(0, 'RIFF'); v.setUint32(4, 36 + dataSize, true); w(8, 'WAVE'); w(12, 'fmt ');
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, numCh, true);
  v.setUint32(24, sr, true); v.setUint32(28, sr * numCh * 2, true);
  v.setUint16(32, numCh * 2, true); v.setUint16(34, 16, true); w(36, 'data'); v.setUint32(40, dataSize, true);
  const chs = [];
  for (let c = 0; c < numCh; c++) chs.push(buffer.getChannelData(c));
  let off = 44;
  for (let i = 0; i < samples; i++) {
    for (let c = 0; c < numCh; c++) {
      const s = Math.max(-1, Math.min(1, chs[c][i]));
      v.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
      off += 2;
    }
  }
  return new Blob([ab], { type: 'audio/wav' });
}

/** Slice an AudioBuffer between [startSec, endSec] into a new AudioBuffer (same SR/channels). */
export function sliceAudioBuffer(ctx, buffer, startSec, endSec) {
  const sr = buffer.sampleRate;
  const s1 = Math.max(0, Math.floor(startSec * sr));
  const s2 = Math.min(buffer.length, Math.floor(endSec * sr));
  const len = Math.max(1, s2 - s1);
  const out = ctx.createBuffer(buffer.numberOfChannels, len, sr);
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const src = buffer.getChannelData(c);
    out.getChannelData(c).set(src.subarray(s1, s2));
  }
  return out;
}

/** Decode an audio URL (mp3/wav/etc.) into an AudioBuffer using an existing AudioContext. */
export async function decodeAudioUrl(ctx, url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch audio: ${res.status}`);
  const ab = await res.arrayBuffer();
  return ctx.decodeAudioData(ab);
}
